/**
 * Data layer for `custom-attribution-windows` (FS-249).
 *
 * Unlike a standard CRUD resource, the backend exposes a per-tenant singleton
 * attribution-window config: click-lookback days, view-lookback days, and a
 * preset label. The API surface is:
 *
 *   GET  /api/v1/custom-attribution-windows          -> WindowView (singleton)
 *   POST /api/v1/custom-attribution-windows          -> upsert (preset OR explicit days)
 *   POST /api/v1/custom-attribution-windows/reset    -> revert to defaults
 *   GET  /api/v1/custom-attribution-windows/presets  -> list of named presets
 *
 * Raw `fetch()` is confined to this file per dash contract rule D6. All
 * requests send `x-tenant-id: t1` and `credentials: 'include'`.
 */

export type PresetName =
	| "default"
	| "conservative"
	| "aggressive"
	| "longform"
	| "extended"
	| "custom";

export interface WindowView {
	tenant_id: string;
	click_window_days: number;
	view_window_days: number;
	preset: PresetName | string;
	created_at: number;
	updated_at: number;
}

export interface Preset {
	name: PresetName | string;
	click_window_days: number;
	view_window_days: number;
}

export interface PresetsResponse {
	presets: Preset[];
}

export interface UpsertWindowBody {
	click_window_days?: number;
	view_window_days?: number;
	preset?: string;
}

const BASE = "/api/v1/custom-attribution-windows";

const TENANT_HEADERS = {
	accept: "application/json",
	"x-tenant-id": "t1",
} as const;

const TENANT_POST_HEADERS = {
	...TENANT_HEADERS,
	"content-type": "application/json",
} as const;

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<WindowView> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: TENANT_HEADERS,
	});
	return parseOrThrow<WindowView>(res);
}

export async function listPresets(): Promise<PresetsResponse> {
	const res = await fetch(`${BASE}/presets`, {
		method: "GET",
		credentials: "include",
		headers: TENANT_HEADERS,
	});
	return parseOrThrow<PresetsResponse>(res);
}

export async function create(body: UpsertWindowBody): Promise<WindowView> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: TENANT_POST_HEADERS,
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<WindowView>(res);
}

export async function remove(): Promise<WindowView> {
	// Backend models "delete" as a hard reset to default preset.
	const res = await fetch(`${BASE}/reset`, {
		method: "POST",
		credentials: "include",
		headers: TENANT_POST_HEADERS,
		body: "{}",
	});
	return parseOrThrow<WindowView>(res);
}

export const customAttributionWindowsQueryKeys = {
	all: ["custom-attribution-windows"] as const,
	list: () => [...customAttributionWindowsQueryKeys.all] as const,
	presets: () =>
		[...customAttributionWindowsQueryKeys.all, "presets"] as const,
	detail: (id: string) =>
		[...customAttributionWindowsQueryKeys.all, id] as const,
};
